import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../lib/axios';
import type { Location, UserLocation } from '../types';

// Fetch all locations for the organization
const fetchLocations = async (): Promise<Location[]> => {
  const response = await axiosInstance.get('/locations');
  // Handle paginated response
  if (response.data.data) {
    return response.data.data;
  }
  return response.data;
};

// Fetch user's assigned locations
const fetchUserLocations = async (userId: string): Promise<UserLocation[]> => {
  const response = await axiosInstance.get(`/permissions/users/${userId}/locations`);
  return response.data;
};

// Assign locations to a user
const assignUserLocations = async ({
  userId,
  locationIds,
}: {
  userId: string;
  locationIds: string[];
}): Promise<void> => {
  await axiosInstance.post(`/permissions/users/${userId}/locations`, { locationIds });
};

// Hooks
export const useLocations = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['locations'],
    queryFn: fetchLocations,
    enabled: options?.enabled ?? true,
  });
};

export const useUserLocations = (userId: string) => {
  return useQuery({
    queryKey: ['userLocations', userId],
    queryFn: () => fetchUserLocations(userId),
    enabled: !!userId,
  });
};

export const useAssignUserLocations = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: assignUserLocations,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['userLocations', variables.userId] });
      queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
  });
};
